import { useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { Menu, X, Phone, ChevronDown } from "lucide-react";
import { Button } from "./ui/button";

export const Header = () => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [servicesOpen, setServicesOpen] = useState(false);
  const location = useLocation();

  const navigation = [
    { name: "Home", path: "/" },
    { name: "About", path: "/about" },
    { 
      name: "Services", 
      path: "/services",
      dropdown: [
        { name: "Intelligent Heating", path: "/intelligent-heating" },
        { name: "Design & Technical", path: "/design-technical" },
        { name: "Smart Plumbing", path: "/smart-plumbing" },
        { name: "Systems Design", path: "/systems-design" }
      ]
    },
    { name: "Maintenance", path: "/maintenance-contracts" },
    { name: "Commercial", path: "/commercial" },
    { name: "Gallery", path: "/gallery" },
    { name: "Recruitment", path: "/recruitment" },
    { name: "Contact", path: "/contact" }
  ];

  const isActive = (path) => location.pathname === path;

  return (
    <header className="fixed top-0 left-0 right-0 z-50 bg-gradient-to-b from-navy-900 via-navy-900/98 to-navy-900/95 backdrop-blur-xl border-b border-white/5 shadow-2xl">
      <div className="container mx-auto px-6 lg:px-20">
        <div className="flex items-center h-20 gap-12">
          
          {/* Logo - Ultra Premium, GUARANTEED One Line */}
          <Link to="/" className="flex-shrink-0 group">
            <h1 className="text-2xl xl:text-3xl font-bold tracking-tight whitespace-nowrap transition-all duration-300 group-hover:scale-[1.02]" style={{ textShadow: '2px 2px 4px rgba(0,0,0,0.8)' }}>
              <span className="text-white font-serif">Magic C </span>
              <span className="text-sage-400 font-serif">Plumbing</span>
            </h1>
          </Link>

          {/* Desktop Navigation - Ultra Premium with Better Spacing */}
          <nav className="hidden lg:flex items-center gap-8 flex-1">
            {navigation.map((item) => (
              item.dropdown ? (
                <div 
                  key={item.name}
                  className="relative group"
                  onMouseEnter={() => setServicesOpen(true)}
                  onMouseLeave={() => setServicesOpen(false)}
                >
                  <button
                    className={`flex items-center gap-1.5 text-sm font-semibold tracking-wide transition-all duration-300 ${
                      isActive(item.path) || item.dropdown.some(sub => isActive(sub.path)) 
                        ? "text-sage-400" 
                        : "text-gray-100 hover:text-white"
                    }`}
                    style={{ textShadow: '1px 1px 3px rgba(0,0,0,0.7)' }}
                  >
                    {item.name}
                    <ChevronDown className={`h-3.5 w-3.5 transition-transform duration-300 ${servicesOpen ? 'rotate-180' : ''}`} />
                  </button>
                  
                  {/* Premium Dropdown */}
                  <div className={`absolute top-full left-0 mt-5 w-64 bg-navy-800/98 backdrop-blur-2xl rounded-xl shadow-2xl border border-white/10 overflow-hidden transition-all duration-300 ${
                    servicesOpen ? 'opacity-100 visible translate-y-0' : 'opacity-0 invisible -translate-y-3'
                  }`}>
                    {item.dropdown.map((subItem) => (
                      <Link
                        key={subItem.name}
                        to={subItem.path}
                        className={`block px-6 py-3.5 text-sm font-medium transition-all duration-200 ${
                          isActive(subItem.path) 
                            ? "text-sage-500 bg-sage-500/10" 
                            : "text-gray-300 hover:text-white hover:bg-white/5"
                        }`}
                      >
                        {subItem.name}
                      </Link>
                    ))}
                  </div>
                </div>
              ) : (
                <Link
                  key={item.name}
                  to={item.path}
                  className={`text-sm font-semibold tracking-wide transition-all duration-300 ${
                    isActive(item.path) ? "text-sage-400" : "text-gray-100 hover:text-white"
                  }`}
                  style={{ textShadow: '1px 1px 3px rgba(0,0,0,0.7)' }}
                >
                  {item.name}
                </Link>
              )
            ))}
          </nav>

          {/* Right Side - Premium CTAs with MORE Spacing */}
          <div className="hidden lg:flex items-center gap-8 flex-shrink-0">
            <a 
              href="tel:07398873176"
              className="group flex items-center gap-3 px-6 py-3 rounded-lg border-2 border-red-500/70 text-red-400 hover:bg-red-500 hover:text-white hover:border-red-500 transition-all duration-300 text-sm font-semibold shadow-lg hover:shadow-red-500/30"
            >
              <Phone className="h-4 w-4 transition-transform group-hover:scale-110" />
              <span>Emergency</span>
            </a>
            
            <Link to="/contact">
              <Button 
                className="bg-gradient-to-r from-sage-600 to-sage-500 hover:from-sage-500 hover:to-sage-400 text-white text-sm font-semibold px-8 py-3 shadow-lg shadow-sage-600/30 hover:shadow-sage-500/40 transition-all duration-300"
              >
                Free Quote
              </Button>
            </Link>
          </div>

          {/* Mobile Menu Button */}
          <button
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            className="lg:hidden p-3 rounded-lg text-gray-300 hover:text-white hover:bg-white/10 transition-all duration-300"
          >
            {mobileMenuOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
          </button>
        </div>

        {/* Mobile Navigation - Premium */}
        {mobileMenuOpen && (
          <div className="lg:hidden py-6 border-t border-white/5 animate-in slide-in-from-top duration-300">
            <nav className="flex flex-col gap-2">
              {navigation.map((item) => (
                item.dropdown ? (
                  <div key={item.name} className="space-y-2">
                    <Link
                      to={item.path}
                      onClick={() => setMobileMenuOpen(false)}
                      className={`block px-5 py-3.5 text-sm font-medium rounded-lg transition-all ${
                        isActive(item.path) ? "text-sage-500 bg-sage-500/10" : "text-gray-300 hover:bg-white/5"
                      }`}
                    >
                      {item.name}
                    </Link>
                    <div className="pl-6 space-y-2">
                      {item.dropdown.map((subItem) => (
                        <Link
                          key={subItem.name}
                          to={subItem.path}
                          onClick={() => setMobileMenuOpen(false)}
                          className={`block px-4 py-3 text-sm rounded-lg transition-all ${
                            isActive(subItem.path) ? "text-sage-500 bg-sage-500/10" : "text-gray-400 hover:text-white hover:bg-white/5"
                          }`}
                        >
                          {subItem.name}
                        </Link>
                      ))}
                    </div>
                  </div>
                ) : (
                  <Link
                    key={item.name}
                    to={item.path}
                    onClick={() => setMobileMenuOpen(false)}
                    className={`block px-5 py-3.5 text-sm font-medium rounded-lg transition-all ${
                      isActive(item.path) ? "text-sage-500 bg-sage-500/10" : "text-gray-300 hover:bg-white/5"
                    }`}
                  >
                    {item.name}
                  </Link>
                )
              ))}
              
              {/* Mobile CTAs */}
              <div className="pt-4 space-y-3 border-t border-white/5 mt-4">
                <a href="tel:07398873176">
                  <Button variant="outline" className="w-full border-2 border-red-500/70 text-red-400 hover:bg-red-500 hover:text-white font-semibold py-3">
                    <Phone className="h-4 w-4 mr-2" />
                    Emergency Call
                  </Button>
                </a>
                
                <Link to="/contact" onClick={() => setMobileMenuOpen(false)}>
                  <Button className="w-full bg-gradient-to-r from-sage-600 to-sage-500 hover:from-sage-500 hover:to-sage-400 text-white font-semibold py-3">
                    Get Free Quote
                  </Button>
                </Link>
              </div>
            </nav>
          </div>
        )}
      </div>
    </header>
  );
};
